import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Phone, MapPin, MessageSquare, Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { id: 'pending', label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' },
  { id: 'confirmed', label: 'Confirmée', color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  { id: 'preparing', label: 'En préparation', color: 'bg-purple-500/20 text-purple-400 border-purple-500/40' },
  { id: 'delivering', label: 'En livraison', color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' },
  { id: 'delivered', label: 'Livrée', color: 'bg-green-500/20 text-green-400 border-green-500/40' },
  { id: 'cancelled', label: 'Annulée', color: 'bg-red-500/20 text-red-400 border-red-500/40' },
];

const STATUS_MAP = STATUS_OPTIONS.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

const PedidosPanel = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active, all, today
  const [now, setNow] = useState(new Date());

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/delivery/orders/display`);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(() => {
      fetchOrders();
      setNow(new Date());
    }, 20000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/delivery/orders/${orderId}/status/public?status=${newStatus}`);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (e) {
      console.error('Status update failed', e);
    }
  };

  const today = now.toISOString().split('T')[0];
  const isSameDay = (createdAt) => createdAt && createdAt.split('T')[0] === today;

  const filtered = orders.filter(o => {
    if (filter === 'today') return isSameDay(o.created_at);
    if (filter === 'active') return !['delivered', 'cancelled'].includes(o.status);
    return true;
  });

  const todayCount = orders.filter(o => isSameDay(o.created_at)).length;
  const activeCount = orders.filter(o => !['delivered', 'cancelled'].includes(o.status)).length;
  const pendingCount = orders.filter(o => o.status === 'pending').length;
  const totalRevenue = orders.filter(o => isSameDay(o.created_at) && o.status !== 'cancelled').reduce((s, o) => s + (o.total || 0), 0);

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 md:p-6" data-testid="pedidos-panel">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl md:text-2xl text-white font-semibold tracking-wide">BÉNI — Commandes Livraison</h1>
            <p className="text-white/40 text-xs mt-1">
              Mis à jour : {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <button onClick={fetchOrders} className="text-[#d4af37] text-sm border border-[#d4af37]/30 px-3 py-1.5 rounded hover:bg-[#d4af37]/10 transition" data-testid="pedidos-refresh-btn">
            Actualiser
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-[#121212] border border-white/10 p-3 rounded-lg text-center">
            <p className="text-2xl text-white font-bold">{todayCount}</p>
            <p className="text-white/50 text-xs">Aujourd'hui</p>
          </div>
          <div className="bg-[#121212] border border-white/10 p-3 rounded-lg text-center">
            <p className="text-2xl text-[#d4af37] font-bold">{activeCount}</p>
            <p className="text-white/50 text-xs">En cours</p>
          </div>
          <div className="bg-[#121212] border border-white/10 p-3 rounded-lg text-center">
            <p className="text-2xl text-yellow-400 font-bold">{pendingCount}</p>
            <p className="text-white/50 text-xs">En attente</p>
          </div>
          <div className="bg-[#121212] border border-white/10 p-3 rounded-lg text-center">
            <p className="text-2xl text-green-400 font-bold">{totalRevenue.toFixed(0)}€</p>
            <p className="text-white/50 text-xs">CA du jour</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          {[
            { id: 'active', label: 'En cours' },
            { id: 'today', label: "Aujourd'hui" },
            { id: 'all', label: 'Tout (7j)' }
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-sm rounded transition ${
                filter === f.id
                  ? 'bg-[#d4af37] text-black font-medium'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
              data-testid={`pedidos-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Orders List */}
        <div className="space-y-2">
          <AnimatePresence>
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingBag className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">Aucune commande</p>
              </div>
            ) : (
              filtered.map((o, idx) => {
                const status = STATUS_MAP[o.status] || STATUS_MAP.pending;
                const isCancelledOrDone = ['delivered', 'cancelled'].includes(o.status);
                const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.address)}`;
                return (
                  <motion.div
                    key={o.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className={`bg-[#121212] border rounded-lg p-3 md:p-4 ${
                      o.status === 'pending' ? 'border-yellow-500/40' :
                      o.status === 'delivered' ? 'border-green-500/30 opacity-60' :
                      o.status === 'cancelled' ? 'border-red-500/20 opacity-50' :
                      'border-white/10'
                    }`}
                    data-testid={`pedidos-order-${o.id}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-2 h-2 rounded-full shrink-0 mt-2 ${
                        o.status === 'pending' ? 'bg-yellow-400 animate-pulse' :
                        o.status === 'delivering' ? 'bg-orange-400 animate-pulse' :
                        o.status === 'delivered' ? 'bg-green-500' :
                        o.status === 'cancelled' ? 'bg-red-500' :
                        'bg-blue-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-medium text-sm">{o.customer_name}</p>
                          <span className="text-[#d4af37] font-bold text-sm">{o.total?.toFixed(2)}€</span>
                          <span className="text-white/40 text-xs">{formatTime(o.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-3 text-white/50 text-xs mt-1 flex-wrap">
                          <a href={`tel:${o.phone}`} className="flex items-center gap-1 hover:text-[#d4af37] transition">
                            <Phone className="w-3 h-3" />{o.phone}
                          </a>
                          <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[#d4af37] transition">
                            <MapPin className="w-3 h-3" />{o.address}
                            <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        </div>
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium border ${status.color}`}>
                        {status.label}
                      </span>
                    </div>

                    {/* Items */}
                    <div className="pl-5 mb-2">
                      <div className="text-white/60 text-xs space-y-0.5">
                        {o.items?.map((it, i) => (
                          <div key={i}>
                            <div>
                              <span className="text-[#d4af37]">{it.quantity}×</span> {it.name_fr} <span className="text-white/30">— {(it.price * it.quantity).toFixed(2)}€</span>
                            </div>
                            {it.notes && (
                              <div className="pl-3 text-yellow-400/70 italic text-[10px]">↳ {it.notes}</div>
                            )}
                          </div>
                        ))}
                      </div>
                      {o.delivery_fee > 0 && (
                        <div className="text-white/40 text-[10px] mt-1">
                          Sous-total: {(o.subtotal || (o.total - (o.delivery_fee || 0))).toFixed(2)}€ + livraison: {o.delivery_fee.toFixed(2)}€
                        </div>
                      )}
                      {o.notes && (
                        <div className="flex items-start gap-1 mt-2 text-yellow-400/80 text-xs">
                          <MessageSquare className="w-3 h-3 shrink-0 mt-0.5" />
                          <span className="italic">{o.notes}</span>
                        </div>
                      )}
                    </div>

                    {/* Quick status actions */}
                    {!isCancelledOrDone && (
                      <div className="pl-5 flex flex-wrap gap-1.5 pt-2 border-t border-white/5">
                        {STATUS_OPTIONS.filter(s => s.id !== o.status).map(s => (
                          <button
                            key={s.id}
                            onClick={() => updateStatus(o.id, s.id)}
                            className={`text-xs px-2 py-1 rounded border transition hover:opacity-80 ${s.color}`}
                            data-testid={`pedidos-action-${o.id}-${s.id}`}
                          >
                            → {s.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default PedidosPanel;
